import { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import api, { getScoutingPin, setScoutingPin } from "@/lib/api";
import { useAreaOpen } from "@/hooks/useAreaOpen";
import { useAuth } from "@/context/AuthContext";
import { canAccessArea } from "@/lib/areas";
import AreaDenied from "@/components/AreaDenied";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LockKey, Binoculars } from "@phosphor-icons/react";
import { ScoutingDomainProvider, useScoutingDomain } from "@/context/ScoutingDomainContext";
import { useLang } from "@/context/LanguageContext";

const PlayerIcon = ({ src }) => (
  <span
    aria-hidden="true"
    className="inline-block w-5 h-5 bg-current shrink-0"
    style={{
      WebkitMaskImage: `url(${src})`,
      maskImage: `url(${src})`,
      WebkitMaskRepeat: "no-repeat",
      maskRepeat: "no-repeat",
      WebkitMaskPosition: "center",
      maskPosition: "center",
      WebkitMaskSize: "contain",
      maskSize: "contain",
    }}
  />
);

function ScoutingDomainBar() {
  const { domain, setDomain } = useScoutingDomain();
  const { tr } = useLang();
  return (
    <div className="flex items-center justify-end gap-2 mb-4" data-testid="scouting-domain-bar">
      <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground mr-1">{tr("Database", "Database")}</span>
      <div className="inline-flex rounded-sm border border-border overflow-hidden">
        <button onClick={() => setDomain("women")} data-testid="domain-women"
          className={`px-3 py-1.5 text-xs font-display uppercase tracking-wide flex items-center gap-2 transition-colors ${domain === "women" ? "bg-primary text-primary-foreground" : "bg-transparent text-muted-foreground hover:text-foreground"}`}>
          <PlayerIcon src="/icons/player-women.png" /> {tr("Femminile", "Women")}
        </button>
        <button onClick={() => setDomain("men")} data-testid="domain-men"
          className={`px-3 py-1.5 text-xs font-display uppercase tracking-wide flex items-center gap-2 transition-colors ${domain === "men" ? "bg-sky-500 text-white" : "bg-transparent text-muted-foreground hover:text-foreground"}`}>
          <PlayerIcon src="/icons/player-men.png" /> {tr("Maschile", "Men")}
        </button>
      </div>
    </div>
  );
}

export function useScoutingUnlocked() {
  const { user } = useAuth();
  const noPin = user && typeof user === "object" && user.org && user.org.pins_required && user.org.pins_required.scouting === false;
  const [unlocked, setUnlocked] = useState(!!getScoutingPin());
  useEffect(() => {
    const on = () => setUnlocked(!!getScoutingPin());
    window.addEventListener("scouting-locked", on);
    window.addEventListener("scouting-unlocked", on);
    return () => { window.removeEventListener("scouting-locked", on); window.removeEventListener("scouting-unlocked", on); };
  }, []);
  return unlocked || !!noPin;
}

export default function ScoutingGate() {
  const { user } = useAuth();
  const { tr } = useLang();
  const unlocked = useScoutingUnlocked();
  const areaState = useAreaOpen("scouting");
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!pin.trim()) return;
    setBusy(true); setError("");
    try {
      await api.post("/scouting/unlock", { pin: pin.trim() });
      setScoutingPin(pin.trim());
      setPin("");
    } catch (err) {
      setError(err.response?.status === 403 ? tr("PIN errato. Riprova.", "Wrong PIN. Please try again.") : tr("Impossibile verificare il PIN.", "Unable to verify the PIN."));
    } finally { setBusy(false); }
  };

  if (areaState === "checking") return null;
  if (!canAccessArea(user, "scouting")) return <AreaDenied area="scouting" />;
  if (unlocked || areaState === "open") return (
    <ScoutingDomainProvider>
      <ScoutingDomainBar />
      <Outlet />
    </ScoutingDomainProvider>
  );

  return (
    <div className="min-h-[70vh] flex items-center justify-center" data-testid="scouting-gate">
      <form onSubmit={submit} className="w-full max-w-sm border border-border rounded-sm bg-card p-8 space-y-6 relative overflow-hidden">
        <Binoculars size={140} weight="fill" className="absolute -right-8 -bottom-8 text-primary/10" />
        <div className="relative">
          <div className="w-12 h-12 rounded-sm bg-primary/15 border border-primary/40 flex items-center justify-center text-primary mb-4"><LockKey size={26} weight="fill" /></div>
          <p className="uppercase text-xs tracking-[0.2em] font-bold text-primary">{tr("Area riservata", "Restricted area")}</p>
          <h1 className="font-display font-black uppercase tracking-tighter text-3xl mt-1">Scouting</h1>
          <p className="text-sm text-muted-foreground mt-2">{tr("Inserisci il PIN Scouting per accedere a calciatrici, obiettivi, campionati e rosa.", "Enter the scouting PIN to access players, targets, leagues and squad.")}</p>
        </div>
        <div className="space-y-2 relative">
          <Input
            data-testid="scouting-pin-input"
            type="password"
            inputMode="numeric"
            autoFocus
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            placeholder="PIN"
            className="text-center text-2xl tracking-[0.5em] font-display h-14"
          />
          {error && <p className="text-sm text-red-400" data-testid="scouting-pin-error">{error}</p>}
        </div>
        <Button type="submit" disabled={busy} data-testid="scouting-pin-submit" className="w-full font-display uppercase tracking-wide h-11">{busy ? tr("Verifica…", "Checking…") : tr("Sblocca", "Unlock")}</Button>
      </form>
    </div>
  );
}
